import { PatientFormDialog } from "@/dentlab/src/pages-via-dialog/patient/patient.dialog";
import { PatientCommandDialog } from "./patient-info-dialog/components/patient-command-dialog.component";
import { PatientRemoveOrphanDialog } from "./patient-remove-orphan-dialog.component";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { ArrowLeftRight, ChevronDown, UserPlus, X } from "lucide-react";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { useState } from "react";
import { isPatientOrPractitionerEditable } from "./job-page-header/job-page-header.component";
import { Button as ShadcnButton } from "@/dentlab/src/components/ui/button";
import { supabase } from "@/lib/supabase";
import { Logger } from "@/lib/logger/Logger";
import { PatientEntityType } from "@/lib/supabase/supabaseTypes";
import { TooltipWrapper } from "@/dentlab/src/components/ui/tooltip-wrapper";
import {
	InputWrapper,
	INVALID_CONTROL_CLASS,
} from "@/dentlab/src/components/input-wrapper";
import { cn } from "@/dentlab/src/lib/util";

export const JobPatientSelect: React.FC<{
	inputHeight?: number;
	validationError?: string | null;
}> = ({ inputHeight, validationError }) => {
	const { upsertPatient, createPatientFromName, getPatient, patientsLookup } =
		usePatientStore((state) => ({
			upsertPatient: state.upsertPatient,
			createPatientFromName: state.createPatientFromName,
			getPatient: state.getPatient,
			patientsLookup: state.patientsLookup,
		}));

	const {
		job,
		updateJobPatient,
		swapToExistingPatient,
		unlinkPatientFromJob,
		quickInsertPatientId,
		jobDocuments,
	} = useJobStore((state) => ({
		job: state.job,
		updateJobPatient: state.updateJobPatient,
		swapToExistingPatient: state.swapToExistingPatient,
		unlinkPatientFromJob: state.unlinkPatientFromJob,
		quickInsertPatientId: state.quickInsertPatientId,
		jobDocuments: Object.values(state.jobDocuments),
	}));

	const [newPatientFormOpen, setNewPatientFormOpen] = useState(false);
	const [patientCommandDialogOpen, setPatientCommandDialogOpen] =
		useState(false);
	const [orphanDialogOpen, setOrphanDialogOpen] = useState(false);

	const handleOpenPatientCommandDialog = () => {
		setPatientCommandDialogOpen(true);
		if (!isPatientOrPractitionerEditable(jobDocuments)) {
			showNotification({
				message:
					"Der Patient wird nicht in bereits erstellte Auftragsdokumente eingefügt!",
				type: "warning",
			});
		}
	};

	const handlePatientSelect = async (patientId: string) => {
		const patient = await getPatient(patientId);
		if (!patient) {
			showNotification({
				message: "Patient konnte nicht gefunden werden",
				type: "error",
			});
			return;
		}
		if (quickInsertPatientId && quickInsertPatientId !== patientId) {
			await swapToExistingPatient(patient);
		} else {
			updateJobPatient(patient);
		}
		setPatientCommandDialogOpen(false);
	};

	const handleNewBlankPatient = () => {
		setNewPatientFormOpen(true);
	};

	const handleNewPatientWithPrefill = async (nameForPrefill: string) => {
		const patient = await createPatientFromName(nameForPrefill);
		if (patient) {
			updateJobPatient(patient);
		} else {
			showNotification({
				message: "Patient konnte nicht erstellt werden",
				type: "error",
			});
		}
	};

	const handleRemovePatient = async () => {
		const patientId = job?.patient_id;
		const jobId = job?.id;
		if (!patientId || !jobId) return;

		// Quick-insert shortcut: always delete + unlink, no dialog
		if (quickInsertPatientId === patientId) {
			await unlinkPatientFromJob({ deletePatient: true });
			return;
		}

		// Orphan check via Supabase count query
		const { count, error } = await supabase
			.from("jobs")
			.select("id", { count: "exact", head: true })
			.eq("patient_id", patientId)
			.neq("id", jobId);

		if (error) {
			Logger.error(error);
			showNotification({
				message: "Prüfung fehlgeschlagen – Patient bleibt verknüpft",
				type: "error",
			});
			return;
		}

		if ((count ?? 0) === 0) {
			setOrphanDialogOpen(true);
		} else {
			await unlinkPatientFromJob({ deletePatient: false });
		}
	};

	const getPatientLabel = () => {
		if (!job?.patient_id) return "aktueller Patient";
		const p = patientsLookup[job.patient_id];
		if (!p) return "aktueller Patient";
		const full = p as PatientEntityType;
		const name = [full.first_name, full.last_name]
			.filter(Boolean)
			.join(" ");
		const code = full.code;
		if (name && code) return `${name} (${code})`;
		if (name) return name;
		if (code) return code;
		return "aktueller Patient";
	};

	return (
		<>
			<InputWrapper validationError={validationError}>
				{!job?.patient_id ? (
					<ShadcnButton
						variant="outline"
						onClick={handleOpenPatientCommandDialog}
						data-testid="add-patient-button"
						aria-label="Patient hinzufügen"
						aria-invalid={!!validationError}
						className={cn(
							"flex items-center gap-2 w-full justify-center",
							validationError && INVALID_CONTROL_CLASS
						)}
						style={
							inputHeight
								? { height: `${inputHeight}px` }
								: { height: "32px" }
						}
					>
						<UserPlus className="w-4 h-4" />
						<span>Patient hinzufügen</span>
					</ShadcnButton>
				) : (
					<div className="flex min-w-0 w-full gap-1">
						<ShadcnButton
							variant="outline"
							onClick={handleOpenPatientCommandDialog}
							data-testid="change-patient-button"
							aria-invalid={!!validationError}
							className={cn(
								"flex min-w-0 flex-1 items-center justify-center gap-2 overflow-hidden",
								validationError && INVALID_CONTROL_CLASS
							)}
							style={
								inputHeight
									? { height: `${inputHeight}px` }
									: { height: "32px" }
							}
						>
							<ArrowLeftRight className="h-4 w-4 shrink-0" />
							<span className="truncate">Patient ändern</span>
						</ShadcnButton>
						<div className="shrink-0">
							<TooltipWrapper title="Patient entfernen">
								<ShadcnButton
									variant="outline"
									onClick={handleRemovePatient}
									data-testid="remove-patient-button"
									aria-label="Patient entfernen"
									className="flex w-10 items-center justify-center gap-2"
									style={
										inputHeight
											? { height: `${inputHeight}px` }
											: { height: "32px" }
									}
								>
									<X className="h-4 w-4" />
								</ShadcnButton>
							</TooltipWrapper>
						</div>
					</div>
				)}
			</InputWrapper>

			<PatientCommandDialog
				onPatientSelect={handlePatientSelect}
				onNewBlankPatient={handleNewBlankPatient}
				onNewPatientWithPrefill={handleNewPatientWithPrefill}
				open={patientCommandDialogOpen}
				setOpen={() => {
					setPatientCommandDialogOpen(false);
				}}
				clientId={job?.client_id}
			/>
			<PatientFormDialog
				open={newPatientFormOpen}
				onClose={() => {
					setNewPatientFormOpen(false);
				}}
				onSave={(patient) => {
					upsertPatient(patient, false);
					updateJobPatient(patient);
					setNewPatientFormOpen(false);
				}}
			/>
			<PatientRemoveOrphanDialog
				open={orphanDialogOpen}
				onOpenChange={setOrphanDialogOpen}
				patientLabel={getPatientLabel()}
				onUnlinkOnly={async () => {
					setOrphanDialogOpen(false);
					await unlinkPatientFromJob({ deletePatient: false });
				}}
				onDeleteAll={async () => {
					setOrphanDialogOpen(false);
					await unlinkPatientFromJob({ deletePatient: true });
				}}
			/>
		</>
	);
};
